import * as React from 'react';
import { useAuth } from '@/auth/AuthProvider';
import type { Role } from './permissions';
import { asRows, toMyInvite } from './rows';
import { asRpcError, callRpc, type ActionResult, type RpcError } from './rpc';
import type { MyInvite } from './types';

/**
 * The caller's own office invitations (design section 4): `my_member_invites()`, addressed to the
 * session's verified email. Needs NO agency: the invite page and the no-agency screen use it, where the
 * invitee is not a member of anything yet (so it cannot sit on useLiveQuery, which waits on the agency).
 * accept / decline keep the SQLSTATE in the error: the page routes 28000 and 42501 to the mailbox proof.
 * A failed read keeps its error too, so the page can say "could not reach the server" for a network failure.
 */

export interface AcceptResult {
  agencyId: string;
  role: Role | string;
}

export interface UseMyInvites {
  /** idle: nobody is signed in. */
  status: 'idle' | 'loading' | 'error' | 'ready';
  invites: MyInvite[];
  /** The failed read's error while status is 'error'; null otherwise. */
  error: RpcError | null;
  reload(): void;
  accept(inviteId: string): Promise<ActionResult<AcceptResult>>;
  decline(inviteId: string): Promise<ActionResult>;
}

type Loaded =
  | { status: 'idle' | 'loading' }
  | { status: 'error'; error: RpcError }
  | { status: 'ready'; invites: MyInvite[] };

export function useMyInvites(): UseMyInvites {
  const { client, user, loading } = useAuth();
  const userId = user?.id ?? null;
  const [loaded, setLoaded] = React.useState<Loaded>({ status: 'loading' });
  const [nonce, setNonce] = React.useState(0);
  const reload = React.useCallback(() => setNonce((n) => n + 1), []);

  React.useEffect(() => {
    if (loading) {
      setLoaded({ status: 'loading' });
      return;
    }
    if (!userId) {
      setLoaded({ status: 'idle' });
      return;
    }
    let active = true;
    setLoaded({ status: 'loading' });
    void (async () => {
      const { data, error } = await client.rpc('my_member_invites');
      if (!active) return;
      if (error) {
        console.warn('useMyInvites: my_member_invites failed', error);
        setLoaded({ status: 'error', error: asRpcError(error) ?? { code: undefined, message: 'Unknown error' } });
        return;
      }
      setLoaded({ status: 'ready', invites: asRows(data).map(toMyInvite) });
    })();
    return () => {
      active = false;
    };
  }, [client, userId, loading, nonce]);

  const accept = React.useCallback<UseMyInvites['accept']>(
    async (inviteId) => {
      const res = await callRpc<{ agency_id?: string; role?: string } | null>(client, 'member_invite_accept', { p_invite: inviteId });
      if (res.error) return { data: null, error: res.error };
      return { data: { agencyId: String(res.data?.agency_id ?? ''), role: String(res.data?.role ?? '') }, error: null };
    },
    [client],
  );

  const decline = React.useCallback<UseMyInvites['decline']>(
    (inviteId) => callRpc<void>(client, 'member_invite_decline', { p_invite: inviteId }),
    [client],
  );

  return {
    status: loaded.status,
    invites: loaded.status === 'ready' ? loaded.invites : [],
    error: loaded.status === 'error' ? loaded.error : null,
    reload,
    accept,
    decline,
  };
}
